/**
 * t373u: HR documents and right to work on the web (desktop; the phone pages reuse the forms). Calm and compact: a card
 * per thing, muted text for status (no badges). Files open through an audited 60-second link; share codes stay masked
 * until Show (audited, hidden again after 30 seconds).
 */
import { useRef, useState } from 'react';
import { Download, Eye, EyeOff, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  HR_DOC_ACCEPT, HR_DOC_NOTES_MAX, HR_DOC_TYPE_LABEL, HR_DOC_TYPE_SHORT, HR_DOC_TYPES, HR_DOC_VISIBILITY_LABEL, RTW_CHECK_TYPE_LABEL, RTW_CHECK_TYPES, RTW_RESULT_LABEL, RTW_RESULTS,
  hrDate, hrDocDraftErrors, hrDocLine, hrExpiringLine, hrToday, rtwDraftErrors, rtwRows,
  type HrDocType, type HrDocument, type HrDocumentInput, type HrDocumentsView, type HrDocVisibility, type HrRtwCheck, type HrRtwInput, type RtwCheckType, type RtwResult,
} from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { Button, Card, Field, Input, Select, Spinner, Textarea, Toggle } from '@/components/ui';
import { useHrDocActions, useHrDocs, useHrExpiring, useShareCodeReveal } from './docs';
import { EditableHint } from '@/components/ui/EditableHint';

const who3 = (v: HrDocumentsView) => (v.self ? 'you' : v.display_name);
/** "Uploaded by Phoebe Mensah 3 Oct 2026 · Checked by Daniel 4 Oct 2026" (muted). */
export function docByLine(d: HrDocument, v: HrDocumentsView): string {
  return [
    d.self_uploaded ? (v.self ? null : `Uploaded by ${v.display_name}`) : `Uploaded by ${d.uploaded_by_name ?? 'HR'}`,
    d.checked_at ? `Checked${d.checked_by_name ? ` by ${d.checked_by_name}` : ''} ${hrDate(d.checked_at.slice(0, 10))}` : null,
    d.rtw_evidence ? 'Right-to-work evidence' : null,
  ].filter(Boolean).join(' · ');
}

// ------------------------------------------------------------------------------------------------ forms (shared with phones)
export function DocForm({ view, doc, file, onDone, initialType }: { view: HrDocumentsView; doc?: HrDocument; file?: File | null; onDone: () => void; initialType?: HrDocType }) {
  const a = useHrDocActions(view.user_id);
  const [d, setD] = useState<HrDocumentInput>(() => doc
    ? { type: doc.type, expires_on: doc.expires_on, notes: doc.notes, visibility: doc.visibility, checked: Boolean(doc.checked_at) }
    : { type: initialType ?? view.asked[0] ?? 'passport', expires_on: null, notes: null, visibility: 'employee' });
  const [picked, setPicked] = useState<File | null>(file ?? null);
  const errs = hrDocDraftErrors(d);
  const save = async () => {
    const body: HrDocumentInput = { type: d.type, expires_on: d.expires_on || null, notes: d.notes?.trim() || null, ...(view.can.set_visibility ? { visibility: d.visibility } : {}) };
    if (doc && view.can.manage && Boolean(doc.checked_at) !== Boolean(d.checked)) body.checked = Boolean(d.checked);
    const r = doc ? await a.update(doc, body) : picked ? await a.upload(picked, body) : null;
    if (r) onDone();
  };
  return (
    <div className="space-y-3" data-testid="hr-doc-form">
      {!doc && <Field label="File" hint={<span className="text-xs text-slate-500">A PDF or a photo, up to 50 MB</span>}>
        <input type="file" accept={HR_DOC_ACCEPT} className="block w-full text-sm" onChange={e => setPicked(e.target.files?.[0] ?? null)} data-testid="hr-doc-file" />
      </Field>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="What is it"><Select value={d.type} onChange={e => setD({ ...d, type: e.target.value as HrDocType })} data-testid="hr-doc-type">
          {HR_DOC_TYPES.map(t => <option key={t} value={t}>{HR_DOC_TYPE_LABEL[t]}</option>)}</Select></Field>
        <Field label="Expires (optional)"><Input type="date" value={d.expires_on ?? ''} onChange={e => setD({ ...d, expires_on: e.target.value || null })} data-testid="hr-doc-expires" /></Field>
        {view.can.set_visibility && <Field label="Who can see it"><Select value={d.visibility} onChange={e => setD({ ...d, visibility: e.target.value as HrDocVisibility })} data-testid="hr-doc-visibility">
          <option value="employee">{view.self ? 'You and HR' : `${view.display_name} and HR`}</option><option value="confidential">{HR_DOC_VISIBILITY_LABEL.confidential}</option></Select></Field>}
        <Field label="Notes (optional)" className="sm:col-span-2"><Textarea rows={2} maxLength={HR_DOC_NOTES_MAX} value={d.notes ?? ''} onChange={e => setD({ ...d, notes: e.target.value })} data-testid="hr-doc-notes" /></Field>
      </div>
      {doc && view.can.manage && <Toggle checked={Boolean(d.checked)} onChange={v => setD({ ...d, checked: v })} label="Checked against the original" />}
      {errs.length > 0 && <ul className="text-sm text-rose-700" role="alert">{errs.map(e => <li key={e}>{e}</li>)}</ul>}
      <div className="flex gap-2">
        <Button size="sm" loading={a.busy} disabled={a.busy || errs.length > 0 || (!doc && !picked)} onClick={() => void save()} data-testid="hr-doc-save">{doc ? 'Save' : 'Upload'}</Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

export function RtwForm({ view, onDone }: { view: HrDocumentsView; onDone: () => void }) {
  const a = useHrDocActions(view.user_id);
  const today = hrToday();
  const [d, setD] = useState<HrRtwInput>({ check_type: 'online', checked_on: today, share_code: '', result: 'unlimited', follow_up_on: null, evidence_document_id: null, notes: '' });
  const [touched, setTouched] = useState(false);
  const errs = touched ? rtwDraftErrors(d, today) : [];
  const save = async () => {
    setTouched(true);
    if (rtwDraftErrors(d, today).length) return;
    const r = await a.addCheck({ ...d, share_code: d.share_code?.trim() || null, follow_up_on: d.result === 'unlimited' ? null : d.follow_up_on || null, notes: d.notes?.trim() || null });
    if (r) onDone();
  };
  return (
    <div className="space-y-3" data-testid="hr-rtw-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="How it was checked"><Select value={d.check_type} onChange={e => setD({ ...d, check_type: e.target.value as RtwCheckType })} data-testid="hr-rtw-type">
          {RTW_CHECK_TYPES.map(t => <option key={t} value={t}>{RTW_CHECK_TYPE_LABEL[t]}</option>)}</Select></Field>
        <Field label="Date checked" hint={<span className="text-xs text-slate-500">Checked by you</span>}><Input type="date" max={today} value={d.checked_on} onChange={e => setD({ ...d, checked_on: e.target.value })} data-testid="hr-rtw-date" /></Field>
        {/* t710u: row 2 is Result (and Permission ends when time-limited); no Evidence picker, files go under Documents. */}
        <Field className="sm:col-start-1" label="Result"><Select value={d.result} onChange={e => setD({ ...d, result: e.target.value as RtwResult })} data-testid="hr-rtw-result">
          {RTW_RESULTS.map(t => <option key={t} value={t}>{RTW_RESULT_LABEL[t]}</option>)}</Select></Field>
        {d.result === 'time_limited' && <Field label="Permission ends (follow up by)"><Input type="date" value={d.follow_up_on ?? ''} onChange={e => setD({ ...d, follow_up_on: e.target.value || null })} data-testid="hr-rtw-follow" /></Field>}
        {d.check_type === 'online' && <Field className="sm:col-start-1" label="Share code" hint={<span className="text-xs text-slate-500">Stored encrypted; shown masked</span>}>
          <Input autoComplete="off" className="uppercase" placeholder="ABC 123 XYZ" maxLength={20} value={d.share_code ?? ''} onChange={e => setD({ ...d, share_code: e.target.value })} data-testid="hr-rtw-code" /></Field>}
        <Field label="Notes (optional)" className="sm:col-span-2"><Textarea rows={2} maxLength={HR_DOC_NOTES_MAX} value={d.notes ?? ''} onChange={e => setD({ ...d, notes: e.target.value })} /></Field>
      </div>
      {errs.length > 0 && <ul className="text-sm text-rose-700" role="alert">{errs.map(e => <li key={e}>{e}</li>)}</ul>}
      <div className="flex gap-2">
        <Button size="sm" loading={a.busy} disabled={a.busy} onClick={() => void save()} data-testid="hr-rtw-save">Save check</Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

/** Delete with a reason (kept with the record); the person deleting their own upload needs none. */
export function DeleteDoc({ view, doc, onDone }: { view: HrDocumentsView; doc: HrDocument; onDone: () => void }) {
  const a = useHrDocActions(view.user_id);
  const [note, setNote] = useState('');
  const needsNote = !view.self;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="hr-doc-delete">
      {needsNote ? <Input className="min-w-48 flex-1" placeholder="Why (kept with the record)" maxLength={300} value={note} onChange={e => setNote(e.target.value)} aria-label="Why" data-testid="hr-doc-delete-note" />
        : <span className="flex-1 text-sm">Delete {doc.file_name}?</span>}
      <Button size="sm" variant="danger" loading={a.busy} disabled={a.busy || (needsNote && !note.trim())} onClick={() => void a.remove(doc, note.trim() || null).then(r => r && onDone())} data-testid="hr-doc-delete-confirm">Delete</Button>
      <Button size="sm" variant="ghost" onClick={onDone}>Keep</Button>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ desktop cards
function DocRow({ d, view }: { d: HrDocument; view: HrDocumentsView }) {
  const a = useHrDocActions(view.user_id);
  const [mode, setMode] = useState<'edit' | 'delete' | null>(null);
  const by = docByLine(d, view);
  return (
    <li className="py-2 text-sm" data-testid={`hr-doc-${d.id}`}>
      <div className="flex items-center gap-3">
        <FileText className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="font-medium">{HR_DOC_TYPE_SHORT[d.type]}</div>
          <div className="truncate text-xs text-slate-500">{hrDocLine(d, hrToday(), !view.self)}{by ? ` · ${by}` : ''}</div>
          {d.notes && <div className="truncate text-xs text-slate-500">{d.notes}</div>}
        </div>
        <Button size="sm" variant="ghost" disabled={a.busy} onClick={() => void a.open(d)} data-testid={`hr-doc-open-${d.id}`}>Open</Button>
        <Button size="icon" variant="ghost" aria-label={`Download ${d.file_name}`} disabled={a.busy} onClick={() => void a.open(d, true)}><Download className="h-4 w-4" /></Button>
        {d.can_edit && <Button size="icon" variant="ghost" aria-label={`Edit ${HR_DOC_TYPE_SHORT[d.type]}`} onClick={() => setMode('edit')} data-testid={`hr-doc-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>}
        {d.can_delete && <Button size="icon" variant="ghost" aria-label={`Delete ${HR_DOC_TYPE_SHORT[d.type]}`} onClick={() => setMode('delete')} data-testid={`hr-doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
      {mode === 'edit' && <div className="mt-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700"><DocForm view={view} doc={d} onDone={() => setMode(null)} /></div>}
      {mode === 'delete' && <div className="mt-2"><DeleteDoc view={view} doc={d} onDone={() => setMode(null)} /></div>}
    </li>
  );
}

export function DocumentsCard({ view }: { view: HrDocumentsView }) {
  const [adding, setAdding] = useState(false);
  return (
    <Card title="Documents" actions={view.can.upload && !adding ? <Button size="sm" variant="ghost" onClick={() => setAdding(true)} data-testid="hr-doc-add"><Plus className="h-4 w-4" />Upload</Button> : undefined}>
      {view.asked.length > 0 && <p className="mb-2 text-sm text-slate-500" data-testid="hr-doc-asked">{view.self ? 'HR asks you to upload' : 'Still to upload'}: {view.asked.map(t => HR_DOC_TYPE_SHORT[t]).join(', ')}</p>}
      {adding && <div className="mb-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700"><DocForm view={view} onDone={() => setAdding(false)} /></div>}
      <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="hr-docs">
        {view.documents.map(d => <DocRow key={d.id} d={d} view={view} />)}
        {!view.documents.length && <li className="py-2 text-sm text-slate-500">No documents yet.</li>}
      </ul>
      {!view.self && view.can.manage && <p className="mt-2 text-xs text-slate-500">Every time a document is opened it’s recorded in the audit log.</p>}
    </Card>
  );
}

function CheckRows({ c, view, rv }: { c: HrRtwCheck; view: HrDocumentsView; rv: ReturnType<typeof useShareCodeReveal> }) {
  const shown = rv.value(c.id);
  return (
    <dl data-testid={`hr-rtw-${c.id}`}>
      {rtwRows(c).map(r => (
        <div key={r.key} className="flex items-baseline gap-3 py-1.5 text-sm">
          <dt className="w-40 shrink-0 text-slate-500">{r.label}</dt>
          <dd className="min-w-0 flex-1 break-words"><span className={shown && r.key === 'share_code' ? 'font-mono' : undefined} data-testid={`hr-rtw-value-${r.key}`}>{r.key === 'share_code' && shown ? shown : r.value}</span>
            {r.key === 'share_code' && shown && <span className="ml-2 text-xs text-slate-500">Hidden again in {rv.left}s</span>}</dd>
          {r.key === 'share_code' && view.can.reveal_share_code && (shown
            ? <button type="button" className="text-xs text-slate-600 underline dark:text-slate-300" onClick={rv.hide}><EyeOff className="mr-1 inline h-3.5 w-3.5" aria-hidden />Hide</button>
            : <button type="button" className="text-xs text-slate-600 underline dark:text-slate-300" onClick={() => void rv.reveal(c.id)} data-testid="hr-rtw-show"><Eye className="mr-1 inline h-3.5 w-3.5" aria-hidden />Show</button>)}
        </div>
      ))}
    </dl>
  );
}

export function RtwCard({ view }: { view: HrDocumentsView }) {
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [note, setNote] = useState('');
  const rv = useShareCodeReveal(view.user_id);
  const a = useHrDocActions(view.user_id);
  const [cur, ...earlier] = view.rtw;
  if (!cur && !view.can.record_rtw) return null;
  return (
    <Card title="Right to work" actions={view.can.record_rtw && !adding ? <Button size="sm" variant="ghost" onClick={() => setAdding(true)} data-testid="hr-rtw-add"><Plus className="h-4 w-4" />{cur ? 'New check' : 'Record a check'}</Button> : undefined}>
      {adding && <div className="mb-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700"><RtwForm view={view} onDone={() => setAdding(false)} /></div>}
      {cur ? <CheckRows c={cur} view={view} rv={rv} /> : !adding && <p className="text-sm text-slate-500" data-testid="hr-rtw-none">No check recorded for {who3(view)} yet.</p>}
      {cur && view.can.manage && (deleting
        ? <div className="mt-2 flex flex-wrap items-center gap-2"><Input className="min-w-48 flex-1" placeholder="Why (kept with the record)" maxLength={300} value={note} onChange={e => setNote(e.target.value)} aria-label="Why" />
          <Button size="sm" variant="danger" disabled={a.busy || !note.trim()} onClick={() => void a.deleteCheck(cur.id, note.trim()).then(r => { if (r) { setDeleting(false); setNote(''); } })}>Delete check</Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleting(false)}>Keep</Button></div>
        : <button type="button" className="mt-1 text-xs text-slate-500 underline" onClick={() => setDeleting(true)}>Delete this check</button>)}
      {earlier.length > 0 && <details className="mt-2 text-xs text-slate-500"><summary className="cursor-pointer">Earlier checks</summary>
        <ul className="mt-1 space-y-0.5">{earlier.map(c => <li key={c.id}>{hrDate(c.checked_on)} · {RTW_RESULT_LABEL[c.result]} · {RTW_CHECK_TYPE_LABEL[c.check_type]}{c.checked_by_name ? ` · ${c.checked_by_name}` : ''}</li>)}</ul></details>}
    </Card>
  );
}

/** Documents + right to work for a record page (yours or someone's). Renders nothing if you can't see them. */
export function HrDocumentsSection({ who }: { who: string }) {
  const q = useHrDocs(who);
  if (q.isLoading) return <Spinner />;
  if (!q.data) return null;
  return <div className="space-y-4" data-testid="hr-documents"><RtwCard view={q.data} /><DocumentsCard view={q.data} /></div>;
}

/** Settings › People › Users › Documents expiring soon (t613u; and the reminder link). Names open the person sheet. */
export function ExpiringList({ onOpenRecord }: { onOpenRecord: (id: string, name: string) => void }) {
  const q = useHrExpiring();
  const openPerson = useUi(s => s.openPerson);
  const ref = useRef(hrToday());
  if (q.isLoading) return <Spinner />;
  if (!q.data) return <p className="text-sm text-slate-500">{errorMessage(q.error)}</p>;
  return (
    <Card>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="hr-expiring">
        {q.data.map(r => (
          <li key={`${r.kind}:${r.id}`} className="flex items-center gap-3 py-2 text-sm" data-testid={`hr-expiring-${r.id}`}>
            <button type="button" className="min-w-0 truncate font-medium hover:underline" onClick={() => openPerson(r.user_id)}>{r.display_name}</button>
            <span className="truncate">{r.label}</span>
            <span className="ml-auto shrink-0 text-slate-500">{hrExpiringLine(r, ref.current)}</span>
            <Button size="sm" variant="ghost" onClick={() => onOpenRecord(r.user_id, r.display_name)}>Open record</Button>
          </li>
        ))}
        {!q.data.length && <li className="py-3 text-sm text-slate-500">Nothing expires in the next 90 days.</li>}
      </ul>
      <EditableHint as="p" id="people.hr.expiry-reminders" className="mt-2 text-xs text-slate-500" default="Reminders go to the people who manage HR documents 60, 30 and 7 days before." />
    </Card>
  );
}
